// views/MainPage/LoginPage/LoginPage.tsx
import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import styles from '../authShared.module.css';
import { notify } from '../../../components/common/Toast/toastBridge';

const GoogleIcon = () => (
  <svg className={styles.googleIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 533.5 544.3">
    <path fill="#4285F4" d="M533.5 278.4c0-17.6-1.6-34.5-4.6-50.9H272v95.9h146.9c-6.4 34.3-26 63.4-55.4 82.9v68h89.5c52.5-48.3 82.5-119.5 82.5-195.9z" />
    <path fill="#34A853" d="M272 544.3c74.2 0 136.6-24.6 182.1-66.8l-89.5-68c-24.8 16.7-56.5 26.5-92.6 26.5-71 0-131.2-48-152.8-112.7h-90v70.8c45.1 89 137.5 150.2 242.8 150.2z" />
    <path fill="#FBBC05" d="M119.2 323.3c-11.4-33.5-11.4-69.5 0-103l-90-70.8c-39 77.6-39 168.3 0 245.9l90-72.1z" />
    <path fill="#EA4335" d="M272 107.7c39.9 0 75.9 13.7 104.2 40.5l78-78C398.5 24.3 337.4 0 272 0 166.7 0 74.3 61.2 29.3 150.2l90 70.8c21.6-64.7 81.8-112.7 152.7-112.7z" />
  </svg>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { onLogin, authError, setAuthError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);

    if (!email || !password) {
      setAuthError('Vyplňte prosím všechna pole');
      setIsLoading(false);
      return;
    }

    try {
      await onLogin({ method: 'email', email, password, rememberMe });
    } catch {
      /* handled by AuthContext (authError) */
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');
    setIsLoading(true);
    try {
      await onLogin({ method: 'google', rememberMe });
    } catch {
      /* handled by AuthContext */
    } finally {
      setIsLoading(false);
    }
  };

  // In-app browsers (Facebook / Messenger) break Google sign-in.
  if (/FBAN|FBAV|Messenger/.test(navigator.userAgent)) {
    return (
      <div className={styles.authPage}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <h1>Nepodporovaný prohlížeč</h1>
              <p className={styles.subtitle}>
                Pro přihlášení do Algapie použijte prosím jiný prohlížeč, například Chrome, Firefox nebo Safari.
              </p>
            </div>
            <div className={styles.error}>
              Prohlížeče integrované v aplikacích jako Facebook nebo Messenger nemusí správně fungovat s naším webem.
            </div>
            <button className={styles.btnPrimary} onClick={() => navigate('/')}>
              Zpět na hlavní stránku
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Přihlášení</h1>
            <p className={styles.subtitle}>Vítejte zpět</p>
          </div>

          {authError && <div className={styles.error}>{authError}</div>}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="vas@email.cz"
                disabled={isLoading}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">Heslo</label>
              <input
                type="password"
                id="password"
                className={styles.input}
                value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
              />
            </div>

            <div className={styles.formFooter}>
              <label className={styles.remember}>
                <input checked={rememberMe} type="checkbox" onChange={() => setRememberMe((v) => !v)} />
                <span>Zapamatovat si mě</span>
              </label>
              <Link to="/forgot-password" className={styles.forgot}>
                Zapomenuté heslo?
              </Link>
            </div>

            <button type="submit" className={styles.btnPrimary} disabled={isLoading}>
              {isLoading ? 'Přihlašování...' : 'Přihlásit se'}
            </button>
          </form>

          <div className={styles.divider}>
            <span>nebo se přihlaste pomocí</span>
          </div>

          <button className={styles.googleBtn} onClick={handleGoogleLogin} disabled={isLoading}>
            <GoogleIcon />
            Continue with Google
          </button>
        </div>

        {/* <div className={`${styles.card} ${styles.registerCard}`}>
          <h2>Nový uživatel</h2>
          <p>
            Připojte se ke spokojeným uživatelům a začněte
            <br />
            vydělávat už dnes!
          </p>
          <Link to="/register" onClick={() => setAuthError('')}>
            <button className={styles.btnPrimary}>Registrace zdarma</button>
          </Link>
        </div> */}
      </div>
    </div>
  );
};

export default LoginPage;